import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const INTEGRATIONS_USERMAPS_PAGED_QUERY_KEY = 'integrations-usermaps-paged';

export enum UserMapMappedStatus {
	All = 'All',
	Mapped = 'Mapped',
	Unmapped = 'Unmapped',
}

export interface IUserMapsFilter {
	search?: string;
	connectionType?: string;
	mappedStatus?: UserMapMappedStatus;
}

export function useInfiniteExternalIntegrationUserMapsQuery({
	enabled = true,
	filter,
	impersonationContext,
	integrationProvider,
	pageSize = 50,
}: {
	enabled?: boolean;
	filter?: IUserMapsFilter;
	impersonationContext?: Api.IImpersonationContext;
	integrationProvider: Api.IntegrationProvider;
	pageSize?: number;
}) {
	const userSession = useUserSession();

	// Body carries the filter; query string carries pagination + (optional) sort.
	// Matches the /activities/filter convention on this surface.
	const filterBody = {
		search: filter?.search || undefined,
		connectionType: filter?.connectionType || undefined,
		// Only send when narrowing — omitting defaults to All on the server.
		mappedStatus:
			filter?.mappedStatus && filter.mappedStatus !== UserMapMappedStatus.All ? filter.mappedStatus : undefined,
	};

	return useInfiniteQuery<Api.IPagedCollection<Api.IIntegrationUser>>({
		enabled,
		refetchOnWindowFocus: false,
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IIntegrationUser>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { pageSize, pageToken: pageParam || undefined },
					urlPath: `integrations/${integrationProvider}/userMaps/filter`,
				}),
				'POST',
				filterBody
			),
		queryKey: [
			INTEGRATIONS_USERMAPS_PAGED_QUERY_KEY,
			integrationProvider,
			filter?.search ?? '',
			filter?.connectionType ?? '',
			filter?.mappedStatus ?? UserMapMappedStatus.All,
			pageSize,
			impersonationContext,
		],
		initialPageParam: '',
		meta: {
			toastErrorMessage: 'Failed to load user mappings',
			errorLogging: {
				category: 'UserMappingsView',
				action: 'LoadUserMaps-Error',
			},
		},
	});
}

export const invalidateInfiniteUserMapsQuery = ({
	impersonationContext,
	integrationProvider,
}: {
	impersonationContext?: Api.IImpersonationContext;
	integrationProvider?: Api.IntegrationProvider;
} = {}) =>
	reactQueryClient.invalidateQueries({
		predicate: query => {
			const [key, provider, , , , , ctx] = query.queryKey as [
				string,
				Api.IntegrationProvider,
				string,
				string,
				UserMapMappedStatus,
				number,
				Api.IImpersonationContext | undefined,
			];
			if (key !== INTEGRATIONS_USERMAPS_PAGED_QUERY_KEY) return false;
			if (integrationProvider !== undefined && provider !== integrationProvider) return false;
			if (impersonationContext !== undefined && ctx?.account?.id !== impersonationContext.account?.id) return false;
			return true;
		},
	});
